/**
 * File Backup Status Component
 * Displays subtle file backup warnings for dispatched orders only
 */

import React, { useState, useEffect } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { getFileBackupStatus } from '../utils/fileUploadIntegration';
import { getBackupStatusMessage } from '../utils/fileRetentionUtils';

const FileBackupStatus = ({ orderId, className = '', orderStatus = null, isDispatchedOnly = false }) => {
  const { organizationId } = useOrganization();
  const [backupStatus, setBackupStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBackupStatus = async () => {
      if (!organizationId || !orderId) return;

      // Only show for dispatched orders if isDispatchedOnly is true
      if (isDispatchedOnly && orderStatus !== 'DISPATCHED') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const status = await getFileBackupStatus(organizationId, orderId);
        setBackupStatus(status);
        setError(null);
      } catch (err) {
        setError('Failed to load backup status');
      } finally {
        setLoading(false);
      }
    };

    fetchBackupStatus();
  }, [organizationId, orderId, orderStatus, isDispatchedOnly]);

  // Don't show anything if loading, error, or no files
  if (loading || error || !backupStatus || !backupStatus.fileBackupInfo || backupStatus.fileBackupInfo.totalFiles === 0) {
    return null;
  }

  // Only show if files are about to be deleted (expiring soon or expired)
  const statusMessage = getBackupStatusMessage(backupStatus.fileBackupInfo, backupStatus.planId);
  if (statusMessage.type !== 'warning' && statusMessage.type !== 'expired') {
    return null;
  }

  return (
    <div className={`file-backup-status subtle-warning ${className}`}>
      <div className="backup-status-message">
        <span className="backup-icon">⚠️</span>
        <span>{statusMessage.message}</span>
      </div>
    </div>
  );
};

export default FileBackupStatus;